import { useState } from 'react'
import { Link, getRouteApi } from '@tanstack/react-router'
import { ExternalLink, ImageOff, MapPin, Star } from 'lucide-react'

import { BarisProduk } from '@/components/baris-produk'
import { PostCard } from '@/components/post-card'
import { ShareButton } from '@/components/share'
import { Avatar } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import { Dialog } from '@/components/ui/dialog'
import { Kolom } from '@/components/layout/kolom'
import { EmptyState } from '@/components/ui/empty-state'
import { Skeleton } from '@/components/ui/skeleton'
import { Tabs } from '@/components/ui/tabs'
import { useToast } from '@/components/ui/toast'
import { useSession } from '@/features/auth/api'
import { useCourses } from '@/features/catalog/api'
import { useExpert } from '@/features/marketplace/api'
import { useServices } from '@/features/services/api'
import { useFollowStats, useToggleFollow, useUserPosts } from '@/features/sosial/api'
import { ApiError } from '@/lib/api'
import { formatNumber, formatRupiah } from '@/lib/format'
import type { TalentPortfolio } from '@/lib/types'

const route = getRouteApi('/authenticated/app/orang/$userId')

type Tab = 'kiriman' | 'portofolio' | 'kelas' | 'jasa'

/**
 * Profil seseorang, dibentuk seperti profil Threads: nama dan foto di atas,
 * satu tombol utama, lalu tab isinya.
 *
 * Profil adalah etalase. Portofolio, kelas, dan jasa sengaja berada di tab
 * yang sama dengan kiriman supaya orang yang tertarik pada tulisan seseorang
 * tinggal satu ketukan dari hasil kerjanya — dan dari cara membayarnya.
 */
export function OrangPage() {
  const { userId } = route.useParams()
  const { data: session } = useSession()
  const { data: profil, isLoading } = useExpert(userId)
  const [tab, setTab] = useState<Tab>('kiriman')

  const diriSendiri = session?.user.id === userId

  if (isLoading) {
    return (
      <Kolom judul="Profil" className="space-y-4 pt-4">
        <Skeleton className="h-40 rounded-2xl" />
        <Skeleton className="h-10 rounded-full" />
      </Kolom>
    )
  }

  if (!profil) {
    return (
      <Kolom judul="Profil">
        <EmptyState title="Profil tidak ditemukan" description="Akun ini mungkin sudah tidak aktif." />
      </Kolom>
    )
  }

  return (
    <Kolom judul="Profil">
      <header className="pt-4">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0 flex-1">
            <h1 className="text-2xl font-extrabold tracking-tight text-ink">{profil.full_name}</h1>
            {profil.headline ? <p className="mt-0.5 text-ink">{profil.headline}</p> : null}
          </div>
          <Avatar name={profil.full_name} src={profil.avatar_url || undefined} size="xl" />
        </div>

        {profil.bio ? (
          <p className="mt-4 whitespace-pre-line leading-relaxed text-ink">{profil.bio}</p>
        ) : null}

        {profil.skills.length > 0 ? (
          <p className="mt-3 text-sm text-muted">{profil.skills.join(' · ')}</p>
        ) : null}

        <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-subtle">
          <Pengikut userId={userId} />
          {profil.city ? (
            <span className="inline-flex items-center gap-1">
              <MapPin className="size-3.5" />
              {profil.city}
            </span>
          ) : null}
          {profil.rating_count > 0 ? (
            <span className="inline-flex items-center gap-1">
              <Star className="size-3.5 fill-amber-400 text-amber-400" />
              {profil.rating.toFixed(1)} · {formatNumber(profil.rating_count)} ulasan
            </span>
          ) : null}
        </div>

        <div className="mt-5 grid grid-cols-2 gap-2">
          {diriSendiri ? (
            <Link to="/app/profil" className="contents">
              <Button variant="outline" size="sm" fullWidth>
                Sunting profil
              </Button>
            </Link>
          ) : (
            <TombolIkuti userId={userId} />
          )}
          <ShareButton
            title={profil.full_name}
            text={`${profil.full_name} di skava.id`}
            url={`${window.location.origin}/talenta/${userId}`}
            className="w-full [&>button]:w-full [&>button]:justify-center"
            label="Bagikan profil"
          />
        </div>
      </header>

      <Tabs
        variant="garis"
        className="-mx-4 mt-6 md:-mx-6"
        value={tab}
        onChange={setTab}
        items={[
          { value: 'kiriman', label: 'Kiriman' },
          { value: 'portofolio', label: 'Portofolio', count: profil.portfolios?.length },
          { value: 'kelas', label: 'Kelas' },
          { value: 'jasa', label: 'Jasa' },
        ]}
      />

      {tab === 'kiriman' ? <TabKiriman userId={userId} diriSendiri={diriSendiri} /> : null}
      {tab === 'portofolio' ? (
        <TabPortofolio karya={profil.portfolios ?? []} diriSendiri={diriSendiri} />
      ) : null}
      {tab === 'kelas' ? <TabKelas userId={userId} diriSendiri={diriSendiri} /> : null}
      {tab === 'jasa' ? <TabJasa userId={userId} diriSendiri={diriSendiri} /> : null}
    </Kolom>
  )
}

function Pengikut({ userId }: { userId: string }) {
  const { data } = useFollowStats(userId)
  if (!data) return null

  return (
    <span>
      <span className="font-semibold text-ink">{formatNumber(data.followers)}</span> pengikut ·{' '}
      <span className="font-semibold text-ink">{formatNumber(data.following)}</span> mengikuti
    </span>
  )
}

function TombolIkuti({ userId }: { userId: string }) {
  const { data } = useFollowStats(userId)
  const ikuti = useToggleFollow(userId)
  const toast = useToast()
  // Selama statusnya belum diketahui, yang tampil hanya tempatnya. Tombol
  // "Ikuti" yang nonaktif lalu berubah menjadi "Mengikuti" di tempat yang
  // sama mengundang ketukan yang justru berhenti mengikuti.
  if (!data) return <Skeleton className="h-9 rounded-full" />

  const mengikuti = data.is_following

  return (
    <Button
      variant={mengikuti ? 'outline' : 'inverted'}
      size="sm"
      fullWidth
      loading={ikuti.isPending}
      onClick={() =>
        ikuti.mutate(mengikuti, {
          onError: (error) =>
            toast.error('Gagal mengubah', error instanceof ApiError ? error.message : undefined),
        })
      }
    >
      {mengikuti ? 'Mengikuti' : 'Ikuti'}
    </Button>
  )
}

function TabKiriman({ userId, diriSendiri }: { userId: string; diriSendiri: boolean }) {
  const { data, isLoading } = useUserPosts(userId)
  const posts = data?.posts ?? []

  if (isLoading) return <Memuat />
  if (posts.length === 0) {
    return (
      <Kosong
        judul={diriSendiri ? 'Anda belum menulis apa pun' : 'Belum ada kiriman'}
        keterangan={
          diriSendiri ? 'Tekan tombol + untuk menceritakan karya yang sedang Anda kerjakan.' : undefined
        }
      />
    )
  }

  return (
    <div>
      {posts.map((post) => (
        <PostCard key={post.id} post={post} />
      ))}
    </div>
  )
}

/**
 * Portofolio sebagai kisi dua kolom. Satu karya dibuka dalam dialog berisi
 * gambar penuh, ceritanya, dan tautannya.
 */
function TabPortofolio({ karya, diriSendiri }: { karya: TalentPortfolio[]; diriSendiri: boolean }) {
  const [dibuka, setDibuka] = useState<TalentPortfolio | null>(null)

  if (karya.length === 0) {
    return (
      <Kosong
        judul={diriSendiri ? 'Portofolio Anda masih kosong' : 'Belum ada karya'}
        keterangan={diriSendiri ? 'Karya yang Anda unggah tampil di sini untuk calon klien.' : undefined}
        aksi={
          diriSendiri ? (
            <Link to="/app/profil">
              <Button variant="inverted" size="sm">
                Tambah karya
              </Button>
            </Link>
          ) : undefined
        }
      />
    )
  }

  return (
    <>
      <div className="grid grid-cols-2 gap-2 pt-4">
        {karya.map((item) => (
          <button
            key={item.id}
            type="button"
            onClick={() => setDibuka(item)}
            className="group overflow-hidden rounded-xl border border-line text-left"
          >
            <div className="aspect-[4/3] overflow-hidden bg-sunken">
              {item.image_url ? (
                <img
                  src={item.image_url}
                  alt=""
                  loading="lazy"
                  className="size-full object-cover transition duration-300 group-hover:scale-[1.03]"
                />
              ) : (
                <div className="grid size-full place-items-center text-subtle">
                  <ImageOff className="size-6" />
                </div>
              )}
            </div>
            <p className="truncate px-3 py-2 text-sm font-semibold text-ink">{item.title}</p>
          </button>
        ))}
      </div>

      {diriSendiri ? (
        <Link to="/app/profil" className="mt-4 block">
          <Button variant="outline" size="sm" fullWidth>
            Kelola portofolio
          </Button>
        </Link>
      ) : null}

      <Dialog open={dibuka !== null} onClose={() => setDibuka(null)} title={dibuka?.title ?? ''} size="lg">
        {dibuka ? (
          <div className="space-y-4">
            {dibuka.image_url ? (
              <img src={dibuka.image_url} alt="" className="w-full rounded-xl border border-line" />
            ) : null}
            {dibuka.description ? (
              <p className="whitespace-pre-line leading-relaxed text-ink">{dibuka.description}</p>
            ) : null}
            {dibuka.url ? (
              <a
                href={dibuka.url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-sm font-semibold text-brand hover:underline"
              >
                Lihat karya aslinya
                <ExternalLink className="size-3.5" />
              </a>
            ) : null}
          </div>
        ) : null}
      </Dialog>
    </>
  )
}

function TabKelas({ userId, diriSendiri }: { userId: string; diriSendiri: boolean }) {
  const { data, isLoading } = useCourses({ expert: userId, sort: 'popular' })
  const kelas = data?.courses ?? []

  if (isLoading) return <Memuat />
  if (kelas.length === 0) {
    return (
      <Kosong
        judul={diriSendiri ? 'Anda belum membuka kelas' : 'Belum ada kelas'}
        keterangan={diriSendiri ? 'Tentukan harganya sendiri; Skava menangani pembayarannya.' : undefined}
        aksi={
          diriSendiri ? (
            <Link to="/app/kelola-kelas">
              <Button variant="inverted" size="sm">
                Buka kelas
              </Button>
            </Link>
          ) : undefined
        }
      />
    )
  }

  return (
    <ul>
      {kelas.map((k) => (
        <BarisProduk
          key={k.id}
          ke="/kelas/$slug"
          slug={k.slug}
          gambar={k.thumbnail_url}
          judul={k.title}
          keterangan={`${formatNumber(k.enrollment_count)} peserta · ${formatNumber(k.lesson_count)} materi`}
          harga={k.effective_price === 0 ? 'Gratis' : formatRupiah(k.effective_price)}
        />
      ))}
    </ul>
  )
}

function TabJasa({ userId, diriSendiri }: { userId: string; diriSendiri: boolean }) {
  const { data, isLoading } = useServices({ seller: userId })
  const jasa = data?.services ?? []

  if (isLoading) return <Memuat />
  if (jasa.length === 0) {
    return (
      <Kosong
        judul={diriSendiri ? 'Anda belum menawarkan jasa' : 'Belum ada jasa'}
        aksi={
          diriSendiri ? (
            <Link to="/app/jasa">
              <Button variant="inverted" size="sm">
                Tawarkan jasa
              </Button>
            </Link>
          ) : undefined
        }
      />
    )
  }

  return (
    <ul>
      {jasa.map((j) => (
        <BarisProduk
          key={j.id}
          ke="/jasa/$slug"
          slug={j.slug}
          gambar={j.thumbnail_url}
          judul={j.title}
          keterangan={j.order_count > 0 ? `${formatNumber(j.order_count)} pesanan` : j.category}
          harga={`Mulai ${formatRupiah(j.starting_price)}`}
        />
      ))}
    </ul>
  )
}

function Memuat() {
  return (
    <div className="space-y-3 pt-4">
      {Array.from({ length: 3 }).map((_, i) => (
        <Skeleton key={i} className="h-20 rounded-2xl" />
      ))}
    </div>
  )
}

function Kosong({ judul, keterangan, aksi }: { judul: string; keterangan?: string; aksi?: React.ReactNode }) {
  return <EmptyState className="py-12" title={judul} description={keterangan} action={aksi} />
}
